import React from 'react';
import { View } from 'react-native';
import { HStack } from '~/components/gluestack/hstack';
import { VStack } from '~/components/gluestack/vstack';
import { Text } from '~/components/gluestack/text';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';

interface ProfileItemProps {
  /** Icon component or icon from HugeIcons to display */
  icon: any;
  /** Label text to display above or beside the value */
  label: string;
  /** Additional CSS classes to apply to the root container */
  className?: string;
  /** Position of the label: 'above' (vertical stack), 'left' (horizontal), or 'hidden' (no label shown) */
  labelPosition?: 'above' | 'left' | 'hidden';
  /** When true, removes flex-1 behavior allowing items to maintain natural width for side-by-side layout */
  compact?: boolean;
  /** The main content/value to display */
  children: React.ReactNode;
}

export function ProfileItem({ icon, label, className = '', labelPosition = 'above', compact = false, children }: ProfileItemProps) {
  const IconComponent = React.isValidElement(icon) ? (
    icon
  ) : (
    <HugeIconsWrapper icon={icon} size={20} className="text-typography-500" />
  );

  // Element children (e.g. badge rows) must not be wrapped in <Text>: views inside
  // Text become inline views sized to the text line box, which clips/misaligns them.
  const isElementContent = React.isValidElement(children);
  const renderValue = (textClassName: string, viewClassName?: string) =>
    isElementContent ? (
      viewClassName ? <View className={viewClassName}>{children}</View> : children
    ) : (
      <Text size="md" className={textClassName}>
        {children}
      </Text>
    );

  return (
    <HStack className={`items-start gap-3 ${className}`}>
      {IconComponent}
      {labelPosition === 'above' ? (
        <VStack className={`gap-1.5 md:gap-0 ${compact ? '' : 'flex-1'}`}>
          <Text size="sm" className="font-medium uppercase text-typography-500 leading-tight">
            {label}
          </Text>
          {renderValue('font-medium')}
        </VStack>
      ) : labelPosition === 'left' ? (
        <HStack className={`items-baseline gap-2 ${compact ? '' : 'flex-1 flex-wrap'}`}>
          <Text size="sm" className="font-medium text-typography-500">
            {label}
          </Text>
          {renderValue(`font-medium ${compact ? '' : 'flex-1'}`, compact ? undefined : 'flex-1')}
        </HStack>
      ) : (
        renderValue(`font-medium ${compact ? '' : 'flex-1'}`, compact ? undefined : 'flex-1')
      )}
    </HStack>
  );
} 